import { FundKind } from '@/modules/fund/types/fund';

/**
 * 基金性质（fundKind）读路径唯一归一点。
 *
 * **wire 值是英文枚举值**（blind_pool / specific / cvc / fund_of_funds /
 * industrial，见后端 fund-kind.enum.ts 与 investment-fund.service.ts:453
 * 「dto.fundKind = normalizeFundKind(...)」/ :684 落枚举值）。中文「直投」
 * 「专项」等只是后端 normalizeFundKind 接受的**输入别名**：写请求可以发中文，
 * 但读回来的 fundKind 永远是英文 ⇒ 任何拿中文字面量与 fundKind 裸相等比较
 * 的写法恒为 false。
 *
 * 本表与后端 normalizeFundKind 逐键同语义；防复发守卫见
 * `fundKind.wire.test.ts`。
 */
export const FUND_KIND_ALIASES: Record<string, FundKind> = {
  直投: FundKind.CVC,
  cvc: FundKind.CVC,
  CVC: FundKind.CVC,
  专项: FundKind.SPECIFIC,
  specific: FundKind.SPECIFIC,
  母基金: FundKind.FUND_OF_FUNDS,
  fund_of_funds: FundKind.FUND_OF_FUNDS,
  产业: FundKind.INDUSTRIAL,
  产业基金: FundKind.INDUSTRIAL,
  industrial: FundKind.INDUSTRIAL,
  盲池: FundKind.BLIND_POOL,
  blind_pool: FundKind.BLIND_POOL,
};

/** 与后端 normalizeFundKind 同语义：空 → 盲池；命中别名 → 英文枚举；未知值原样透传。 */
export const normalizeFundKind = (value?: string | null): string => {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return FundKind.BLIND_POOL;
  // 自有属性才命中（后端是 if-chain，不存在原型链误命中问题）
  if (!Object.hasOwn(FUND_KIND_ALIASES, trimmed)) {
    return trimmed; // 未知值原样透传，与后端 (trimmed as FundKind) || BLIND_POOL 同义
  }
  return FUND_KIND_ALIASES[trimmed];
};

/** 直投资金账户（CVC）读路径唯一谓词：不许拿中文字面量与 fundKind 直接比较。 */
export const isDirectCapitalAccount = (fund: {
  fundKind?: string | null;
}): boolean => normalizeFundKind(fund.fundKind) === FundKind.CVC;
